import { alpha } from "@mui/system"
import { MuiRootProps } from "types/mui"
import boxShadows from "./boxShadows"
import { Dusk, Midnight, Sand } from "./colours"

/**
 * - Styles override to MUIButton to match Vacasa owner onboarding design
 * - Figma link to button design: https://www.figma.com/file/kfXA1bse9Xd24MjmTDOjJo/B2B?node-id=1168%3A2341
 */
const styleOverrides = {
    root: ({ ownerState, theme }: MuiRootProps) => ({
        borderRadius: "40px",
        textTransform: "none",
        fontWeight: 800,
        fontSize: "1rem",
        height: "50px",
        lineHeight: "24px",
        ...(ownerState.size === "small" && {
            fontSize: "0.875rem",
        }),
        ...(ownerState.size === "xSmall" && {
            // button xsmall
            fontWeight: 700,
            fontSize: "0.75rem",
            lineHeight: "0.875rem",
            minWidth: "unset",
        }),
        [theme.breakpoints.up("md")]: {
            height: "48px",
        },
    }),
}

/**
 * Button primary variant based on figma design
 */
const primaryVariant = [
    {
        props: { variant: "primary" },
        style: {
            background: Midnight[100],
            color: "white",
            "&:hover": {
                background: Midnight[70],
            },
            "&:focus": {
                border: `2px solid ${Midnight[20]}`,
                background: Midnight[100],
            },
            "&:active": {
                filter: `drop-shadow(${boxShadows.primaryButtonActive})`,
                background: Midnight[100],
            },
            "&.Mui-disabled": {
                background: Midnight[100],
                opacity: 0.3,
                color: "white",
            },
        },
    },
]

/**
 * Button secondary variant based on figma design
 */
export const secondaryVariant = [
    {
        props: { variant: "secondary" },
        style: {
            height: "40px",
            background: "white",
            color: Midnight[100],
            border: `2px solid ${Midnight[20]}`,
            "&:hover": {
                border: `2px solid ${Midnight[70]}`,
                color: Midnight[70],
                background: "white",
            },
            "&:focus": {
                border: `2px solid ${Midnight[100]}`,
                opacity: 0.8,
                outline: `${Midnight[10]} solid 4px`,
            },
            "&:active": {
                background: alpha("#FCFCFC", 0.15),
                color: Midnight[100],
                border: `2px solid ${Midnight[100]}`,
            },
            "&.Mui-disabled": {
                border: `2px solid ${Midnight[30]}`,
                color: Midnight[30],
            },
            "&.noColorBorder": {
                boxShadow: "1px 1px 5px rgba(0, 0, 0, 0.2)",
                border: "2px solid transparent",
                height: "40px",
                "&:hover": {
                    border: `2px solid ${Midnight[10]}`,
                },
            },
        },
    },
]

/**
 * Button save variant based on figma design
 */
export const containedVariant = [
    {
        props: { variant: "contained" },
        style: {
            height: "26px",
            background: Midnight[100],
            color: "white",
            border: "none",
            "&:hover": {
                background: Midnight[70],
            },
            "&.Mui-disabled": {
                background: Sand[100],
                color: Dusk[100],
            },
        },
    },
]

/**
 * MUI Button with customized styling & variants to match figma
 */
const customizedButton = {
    styleOverrides,
    variants: [...primaryVariant, ...secondaryVariant, ...containedVariant],
}

export default customizedButton
